import { useMemo } from 'react'
import { usePlatform } from '../context/PlatformContext'

/**
 * useAfterTideFilter
 *
 * A shared hook that filters data arrays to 18+ content when on AfterTide.
 * Works with any data shape that has age/spice/darkness indicators.
 *
 * Usage:
 *   const { isAfterTide, filterContent, platformLabel } = useAfterTideFilter()
 *   const books = filterContent(allBooks, { field: 'ageRating', value: '18+' })
 */

export interface FilterConfig {
  /** The field name to check for 18+ eligibility */
  field?: string
  /** The value that indicates 18+ content */
  value?: string | number
  /** Custom filter function for complex eligibility checks */
  customFilter?: (item: any) => boolean
}

// Default fields that indicate 18+ content
const DEFAULT_18_PLUS_FIELDS = [
  { field: 'ageRating', value: '18+' },
  { field: 'spiceLevel', minValue: 4 },
  { field: 'darknessLevel', minValue: 3 },
  { field: 'darknessTier', minValue: 1 },
  { field: 'publishToAfterTide', value: true },
]

/**
 * Check if a single item qualifies as 18+ content
 */
export function is18PlusContent(item: any): boolean {
  if (!item || typeof item !== 'object') return false

  for (const check of DEFAULT_18_PLUS_FIELDS) {
    const val = item[check.field]
    if (val === undefined || val === null) continue

    if ('value' in check && val === check.value) return true
    if (
      'minValue' in check &&
      typeof val === 'number' &&
      val >= (check as any).minValue
    )
      return true
  }

  return false
}

/**
 * Filter an array to only 18+ content
 */
export function filterTo18Plus<T>(items: T[]): T[] {
  return items.filter((item) => is18PlusContent(item))
}

export function useAfterTideFilter() {
  const { isAfterTide } = usePlatform()

  /**
   * Filter content based on platform.
   * On LitLaunch: returns all items
   * On AfterTide: returns only 18+ items
   */
  const filterContent = useMemo(() => {
    return <T,>(items: T[], config?: FilterConfig): T[] => {
      if (!isAfterTide) return items

      if (config?.customFilter) {
        return items.filter(config.customFilter)
      }

      if (config?.field) {
        return items.filter((item: any) => {
          const val = item[config.field!]
          if (config.value !== undefined) return val === config.value
          return !!val
        })
      }

      // Default: use the standard 18+ content check
      return filterTo18Plus(items)
    }
  }, [isAfterTide])

  /**
   * Select between two data sources based on platform
   */
  const platformData = useMemo(() => {
    return <T,>(litLaunchData: T, afterTideData: T): T => {
      return isAfterTide ? afterTideData : litLaunchData
    }
  }, [isAfterTide])

  /**
   * Get platform-appropriate label
   */
  const platformLabel = isAfterTide ? 'AfterTide' : 'LitLaunch'

  /**
   * Get platform-appropriate accent color class
   */
  const accentColor = isAfterTide ? 'text-[#e2b04a]' : 'text-purple-600'
  const accentBg = isAfterTide ? 'bg-[#e2b04a]' : 'bg-purple-600'
  const accentBorder = isAfterTide ? 'border-[#e2b04a]' : 'border-purple-600'

  return {
    isAfterTide,
    filterContent,
    platformData,
    platformLabel,
    accentColor,
    accentBg,
    accentBorder,
    is18PlusContent,
    filterTo18Plus,
  }
}
